import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Edit, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { templateAPI, categoryAPI } from '../../services/api';
import { CategoryLoadingBar, ContentLoadingBar, SearchLoadingBar } from './SidebarLoadingBar';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, showAdminAccessDialog, ADMIN_ACTIONS } from '../../utils/adminUtils';


const TemplatesSidebar = ({ isOpen, onClose, onTemplateSelect }) => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [selectedRootCategory, setSelectedRootCategory] = useState('all');
  const [selectedChildCategory, setSelectedChildCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingChildCategories, setLoadingChildCategories] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#3B82F6' });

  // Refs for horizontal scrolling
  const rootCategoriesRef = useRef(null);
  const childCategoriesRef = useRef(null);

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || 'dev-token-' + Date.now();
  };

  // Fetch templates with pagination
  const fetchTemplates = async (resetTemplates = true, pageNum = 1) => {
    if (resetTemplates) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = {
        page: pageNum,
        limit: 20,
      };

      // Add category filter
      if (selectedChildCategory !== 'all') {
        params.category_id = selectedChildCategory;
      } else if (selectedRootCategory && selectedRootCategory !== 'all') {
        params.category_id = selectedRootCategory;
      }

      // Add search filter
      if (searchQuery) {
        params.search = searchQuery;
      }

      const resp = await templateAPI.getAll(params);
      const data = resp.data;

      if (data.success) {
        const templatesData = Array.isArray(data.data) ? data.data : data.data.templates || [];

        // Debug: Log template properties to see what's available
        if (templatesData.length > 0) {
          console.log('Template properties:', Object.keys(templatesData[0]));
          console.log('Image properties for first template:', {
            thumbnail: templatesData[0].thumbnail,
            sampleImageUrl: templatesData[0].sampleImageUrl,
            previewUrl: templatesData[0].previewUrl,
            thumbnailUrl: templatesData[0].thumbnailUrl
          });
        }

        if (resetTemplates) {
          setTemplates(templatesData);
        } else {
          setTemplates(prev => [...prev, ...templatesData]);
        }

        setHasMore(templatesData.length === params.limit);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      if (resetTemplates) {
        setTemplates([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch root categories (parentId is null)
  const fetchRootCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryAPI.getAll({ type: 'template', parentId: '' });
      const data = response.data;

      if (data.success) {
        const rootCategoriesData = data.data || [];
        // Sort root categories by name
        rootCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setRootCategories(rootCategoriesData);
        // Keep "All Categories" selected by default (no auto-selection)
      }
    } catch (error) {
      console.error('Error fetching root categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch child categories for selected root category
  const fetchChildCategories = async (parentId) => {
    try {
      if (!parentId || parentId === 'all') {
        setChildCategories([]);
        return;
      }

      const response = await categoryAPI.getAll({ type: 'template', parentId });
      const data = response.data;

      if (data.success) {
        const childCategoriesData = data.data || [];
        // Sort child categories by name
        childCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setChildCategories(childCategoriesData);
      }
    } catch (error) {
      console.error('Error fetching child categories:', error);
      setChildCategories([]);
    }
  };

  // Load more templates
  const loadMoreTemplates = () => {
    if (!loadingMore && hasMore) {
      fetchTemplates(false, page + 1);
    }
  };

  // Handle root category selection
  const handleRootCategorySelect = (category_id) => {
    setSelectedRootCategory(category_id);
    setSelectedChildCategory('all'); // Reset child category selection
    setPage(1); // Reset pagination

    // Fetch child categories for the selected root category
    if (category_id && category_id !== 'all') {
      fetchChildCategories(category_id);
    } else {
      setChildCategories([]);
    }
  };

  // Handle child category selection
  const handleChildCategorySelect = (category_id) => {
    setSelectedChildCategory(category_id);
    setPage(1); // Reset pagination
  };

  // Horizontal scroll functions
  const scrollCategories = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 200;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Create category
  const createCategory = async () => {
    // Check admin access
    if (!isAdmin(user)) {
      showAdminAccessDialog(ADMIN_ACTIONS.CREATE_CATEGORY);
      return;
    }

    try {
      await categoryAPI.create({ ...newCategory, type: 'template' });
      setNewCategory({ name: '', description: '', color: '#3B82F6' });
      fetchRootCategories();

      // Show success notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'success',
          message: 'Template category created successfully!'
        }
      }));
    } catch (error) {
      console.error('Error creating category:', error);

      // Show error notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'error',
          message: error.response?.data?.message || 'Failed to create category. Please try again.'
        }
      }));
    }
  };

  // Update category
  const updateCategory = async (id, data) => {
    try {
      const resp = await categoryAPI.update(id, data)
      if (resp.status >= 200 && resp.status < 300) {
        setEditingCategory(null);
        fetchRootCategories();
        // Refresh child categories if we're viewing them
        if (selectedRootCategory && selectedRootCategory !== 'all') {
          fetchChildCategories(selectedRootCategory);
        }
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        const resp = await categoryAPI.delete(id)
        if (resp.status >= 200 && resp.status < 300) {
          fetchRootCategories();
          // Refresh child categories if we're viewing them
          if (selectedRootCategory && selectedRootCategory !== 'all') {
            fetchChildCategories(selectedRootCategory);
          }
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  // Load data when sidebar opens
  useEffect(() => {
    if (isOpen) {
      fetchRootCategories();
    }
  }, [isOpen]);

  // Load child categories when root category changes
  useEffect(() => {
    if (selectedRootCategory && selectedRootCategory !== 'all') {
      fetchChildCategories(selectedRootCategory);
    } else {
      setChildCategories([]);
    }
  }, [selectedRootCategory]);

  // Load templates when filters change
  useEffect(() => {
    if (isOpen) {
      fetchTemplates(true, 1); // Reset templates and start from page 1
    }
  }, [isOpen, selectedRootCategory, selectedChildCategory, searchQuery]);

  if (!isOpen) return null;

  return (
    <>
      {/* Custom CSS for hiding scrollbars */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-semibold">Import Templates</h2>
            <p className="text-xs text-gray-500">Add items from existing designs</p>
          </div>
        </div>
        <button
          onClick={() => setShowCategoryManager(!showCategoryManager)}
          className="p-2 hover:bg-gray-100 rounded"
          title="Manage Categories"
        >
          <Edit className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search designs to import..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="mt-2 text-xs text-gray-600 bg-blue-50 p-2 rounded">
          💡 <strong>Import Mode:</strong> Select a design to merge its items into your current design page by page.
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        {/* Category Manager */}
        {showCategoryManager && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium mb-3">Manage Categories</h3>

            {/* Add New Category */}
            <div className="space-y-2 mb-4">
              <input
                type="text"
                placeholder="Category name"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={newCategory.color}
                  onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                  className="w-10 h-8 border border-gray-300 rounded"
                />
                <button
                  onClick={createCategory}
                  disabled={!newCategory.name}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4 inline mr-1" />
                  Add
                </button>
              </div>
            </div>

            {/* Existing Categories */}
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {rootCategories.map((category) => (
                <div key={category.id} className="flex items-center justify-between p-2 bg-white rounded border">
                  {editingCategory === category.id ? (
                    <div className="flex-1 space-y-1">
                      <input
                        type="text"
                        defaultValue={category.name}
                        onBlur={(e) => updateCategory(category.id, { name: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 flex-1">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-sm">{category.name}</span>
                    </div>
                  )}
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setEditingCategory(editingCategory === category.id ? null : category.id)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deleteCategory(category.id)}
                      className="p-1 hover:bg-gray-100 rounded text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Two-Line Category System */}
        <div className="border-b border-gray-200 bg-gray-50">
          {/* First Line - Root Categories */}
          <div className="px-4 py-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => scrollCategories(rootCategoriesRef, 'left')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                title="Scroll left"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <div
                ref={rootCategoriesRef}
                className="flex space-x-2 overflow-x-auto scrollbar-hide flex-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <button
                  onClick={() => handleRootCategorySelect('all')}
                  className={`px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
                    selectedRootCategory === 'all' || !selectedRootCategory
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  All Categories
                </button>
                {rootCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleRootCategorySelect(category.id)}
                    className={`px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0 flex items-center space-x-1 transition-colors ${
                      selectedRootCategory === category.id
                        ? 'text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                    style={{
                      backgroundColor: selectedRootCategory === category.id ? category.color || '#3B82F6' : undefined,
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor: selectedRootCategory === category.id
                          ? 'white'
                          : category.color || '#3B82F6'
                      }}
                    />
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => scrollCategories(rootCategoriesRef, 'right')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                title="Scroll right"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Second Line - Child Categories */}
          <div className="px-4 pb-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => scrollCategories(childCategoriesRef, 'left')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                title="Scroll left"
              >
                <ChevronLeft className="w-3 h-3 text-gray-500" />
              </button>
              <div
                ref={childCategoriesRef}
                className="flex space-x-2 overflow-x-auto scrollbar-hide flex-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <button
                  onClick={() => handleChildCategorySelect('all')}
                  className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
                    selectedChildCategory === 'all'
                      ? 'bg-green-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  All
                </button>
                {childCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleChildCategorySelect(category.id)}
                    className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap flex-shrink-0 flex items-center space-x-1 transition-colors ${
                      selectedChildCategory === category.id
                        ? 'text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                    style={{
                      backgroundColor: selectedChildCategory === category.id ? category.color || '#10B981' : undefined,
                    }}
                  >
                    <div
                      className="w-1 h-1 rounded-full"
                      style={{
                        backgroundColor: selectedChildCategory === category.id
                          ? 'white'
                          : category.color || '#10B981'
                      }}
                    />
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => scrollCategories(childCategoriesRef, 'right')}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                title="Scroll right"
              >
                <ChevronRight className="w-3 h-3 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="p-4">
          {loading ? (
            <ContentLoadingBar isLoading={loading} message="Loading templates..." />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-1">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => onTemplateSelect(template)}
                    className="cursor-pointer group border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-md transition-all"
                  >
                    <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden mb-2 group-hover:shadow-sm transition-shadow relative">
                      {(template.thumbnail || template.sampleImageUrl || template.previewUrl || template.thumbnailUrl) ? (
                        <img
                          src={template.thumbnail || template.sampleImageUrl || template.previewUrl || template.thumbnailUrl}
                          alt={template.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // If image fails to load, show placeholder
                            e.target.style.display = 'none';
                            const placeholder = e.target.parentElement.querySelector('.placeholder');
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className="placeholder absolute inset-0 w-full h-full flex items-center justify-center text-gray-400"
                        style={{ display: (template.thumbnail || template.sampleImageUrl || template.previewUrl || template.thumbnailUrl) ? 'none' : 'flex' }}
                      >
                        <div className="text-center">
                          <span className="text-xs">No Preview</span>
                          <div className="text-xs text-gray-300 mt-1">{template.name}</div>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1 break-words line-clamp-2">{template.name}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        {template.isPremium && (
                          <span className="text-xs text-yellow-600 bg-yellow-100 px-1 rounded">Premium</span>
                        )}
                        <span className="text-xs text-gray-500">
                          {template.pages?.length || 1} page{(template.pages?.length || 1) !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="text-xs text-blue-600 font-medium">Import</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && templates.length > 0 && (
                <div className="mt-6 text-center">
                  <button
                    onClick={loadMoreTemplates}
                    disabled={loadingMore}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 mx-auto"
                  >
                    {loadingMore ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <span>Load More Templates</span>
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && templates.length === 0 && (
            <div className="text-center text-gray-500 mt-8">
              <p>No designs found to import</p>
              {searchQuery && (
                <p className="text-sm mt-1">Try adjusting your search or category filter</p>
              )}
              {selectedChildCategory !== 'all' && (
                <p className="text-sm mt-1">Try selecting a different category</p>
              )}
              <div className="mt-4 text-xs text-gray-400 bg-gray-50 p-3 rounded">
                <p>💡 <strong>Tip:</strong> Import functionality merges items from existing designs into your current project.</p>
                <p className="mt-1">• Matching pages: Items are combined</p>
                <p>• Extra pages: New pages are created</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default TemplatesSidebar;
